import {assert} from 'Asserts';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {ClientTagSchemaConstraint, RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {FieldType, RequiredLink} from 'schemas/fields/SchemaFieldConstants';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {CollaboratorInfoGraphQL} from '../../../graphql-fragments/auth/__generated/CollaboratorInfoGraphQL';
import {OptionalBaseBioentityType} from './SchemaInterfaceBaseBioentityTypeDropdown';

// Keep in sync with /src/benchling/schemas/schema_interfaces/api/schema_interfaces.py
export const SCHEMA_INTERFACES_BASE_URL = 'fieldsets';
const _SCHEMA_INTERFACE_SUB_URL = 'fieldsets';

export function schemaInterfacesURL(): string {
  return `/${SCHEMA_INTERFACES_BASE_URL}/`;
}

export function createSchemaInterfaceURL(): string {
  return `/${SCHEMA_INTERFACES_BASE_URL}/create`;
}

export function schemaInterfaceURL(interfaceId: string): string {
  return `/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}`;
}

interface SchemaInterfaceFieldForRequest {
  systemName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired: boolean;
  isMulti: boolean;
  requiredLink: RequiredLink | null;
  schemaFieldSelectorId: string | null;
}

export function createSchemaInterface(args: {
  name: string;
  organizationId: string;
  sqlIdentifier: string | null;
  prefix: string;
  labelingStrategies?: Array<RegistryLabelingStrategy>;
  baseBioentityType: OptionalBaseBioentityType;
  constraint?: SchemaConstraintCreatePayload | null;
  collaborators?: Array<SchemaCollaborator>;
  fields?: Array<SchemaInterfaceFieldForRequest>;
}): Promise<{id: string}> {
  return net.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}`,
    data: args,
  });
}

export function convertToSchemaConstraintCreatePayload(
  tagSchemaConstraint: ClientTagSchemaConstraint
): SchemaConstraintCreatePayload {
  const fields = tagSchemaConstraint.fields.map((field) => ({
    name: field.name,
  }));
  return {
    uniqueResidues: tagSchemaConstraint.uniqueResidues,
    uniqueCanonicalSmiles: tagSchemaConstraint.uniqueCanonicalSmiles,
    areUniqueResiduesCaseSensitive: tagSchemaConstraint.areUniqueResiduesCaseSensitive,
    fields,
  };
}

export function convertToSchemaConstraintUpdatePayload(
  tagSchemaConstraint: ClientTagSchemaConstraint
): SchemaConstraintUpdatePayload {
  const fields = tagSchemaConstraint.fields.map((field) => {
    const apiId = field.apiId;
    // TagSchemaConstraint's fields are defined to be a SchemaField | ClientSchemaField, where client schema field may not have an apiId.
    // Through the update entrypoint however, an apiId must be present.
    // Ideally the types would be refactored to make this unnecessary.
    assert(Boolean(apiId), 'Expected field to have an apiId');
    return {
      apiId: apiId!,
      name: field.name,
    };
  });
  return {
    uniqueResidues: tagSchemaConstraint.uniqueResidues,
    uniqueCanonicalSmiles: tagSchemaConstraint.uniqueCanonicalSmiles,
    areUniqueResiduesCaseSensitive: tagSchemaConstraint.areUniqueResiduesCaseSensitive,
    fields,
  };
}

export interface SchemaConstraintCreatePayload {
  uniqueResidues: boolean;
  uniqueCanonicalSmiles: boolean;
  areUniqueResiduesCaseSensitive: boolean;
  fields: Array<{
    name: string;
  }>;
}

export interface SchemaConstraintUpdatePayload {
  uniqueResidues: boolean;
  uniqueCanonicalSmiles: boolean;
  areUniqueResiduesCaseSensitive: boolean;
  fields: Array<{
    apiId: string;
    name: string;
  }>;
}

export interface UpdateSchemaInterfaceRequest {
  name?: string;
  sqlIdentifier?: string;
  baseBioentityType?: OptionalBaseBioentityType;
  prefix?: string;
  labelingStrategies?: Array<RegistryLabelingStrategy>;
  constraint?: SchemaConstraintUpdatePayload | null;
}

export function updateSchemaInterface(
  interfaceId: string,
  data: UpdateSchemaInterfaceRequest
): Promise<{id: string}> {
  return net.patchJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}`,
    data,
  });
}

export function addSchemaInterfaceField({
  interfaceId,
  field,
}: {
  interfaceId: string;
  field: SchemaInterfaceFieldForRequest;
}): Promise<{id: string}> {
  return net.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}/fields`,
    data: {field},
  });
}

export function updateSchemaInterfaceField({
  interfaceId,
  fieldId,
  field,
}: {
  interfaceId: string;
  fieldId: string;
  field: SchemaInterfaceFieldForRequest;
}): JQueryPromise<{id: string}> {
  return TaskRunner.runTask({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}/fields/${fieldId}`,
    data: JSON.stringify({field}),
    type: 'PATCH',
    dataType: 'json',
    contentType: 'application/json',
  });
}

export function removeSchemaInterfaceField(args: {interfaceId: string; fieldId: string}): Promise<{}> {
  return net.deletePromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${args.interfaceId}/fields/${args.fieldId}`,
  });
}

interface UpdateSchemaCollaboratorsRequest {
  collaborators: Array<SchemaCollaborator>;
}
interface UpdateInterfaceCollaboratorsResponse {
  interface: CollaboratorInfoGraphQL;
}

export function updateCollaborators(
  interfaceId: string,
  data: UpdateSchemaCollaboratorsRequest
): Promise<UpdateInterfaceCollaboratorsResponse> {
  return net.patchJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}/collaborators`,
    data,
  });
}

export function bulkArchive(ids: Array<string>, purpose: string): Promise<void> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}:bulk-archive`,
    data: {ids, purpose},
  });
}

export function bulkUnarchive(ids: Array<string>): Promise<void> {
  return net.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}:bulk-unarchive`,
    data: {ids},
  });
}

interface UpdateInterfaceSchemaAssociationsResponse {
  interfaces: Array<string>;
}

export function updateInterfaceSchemaAssociations(
  schemaId: string,
  interfaceIds: Array<string>
): Promise<UpdateInterfaceSchemaAssociationsResponse> {
  return net.postJSONPromise({
    url: `/1/api/schemas/${schemaId}/${_SCHEMA_INTERFACE_SUB_URL}`,
    data: {
      interfaces: interfaceIds.map((interfaceId) => ({apiId: interfaceId})),
    },
  });
}

export function removeInterfaceSchemaAssociation(
  schemaId: string,
  interfaceId: string,
  schemaPrefixReplacement?: string
): Promise<UpdateInterfaceSchemaAssociationsResponse> {
  return TaskRunner.deleteJSONPromise({
    url: `/1/api/schemas/${schemaId}/${_SCHEMA_INTERFACE_SUB_URL}/${interfaceId}`,
    data: {schemaPrefixReplacement},
  });
}

export function setSchemaInterfaceNameTemplate(
  interfaceId: string,
  nameTemplateParts: Array<NameTemplatePart>,
  shouldOrderPartsBySequence: boolean
): Promise<{id: string}> {
  return net.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}/name-template`,
    data: {nameTemplateParts, shouldOrderPartsBySequence},
  });
}
